<template>
  <div
    class="card-item-text h-full min-w-0 w-full overflow-hidden flex flex-col gap-7"
    :class="[horizontalPaddingClass, verticalPaddingClass]"
    :style="{
      color: card.textColor || '#414141',
      backgroundColor: card.bgColor || '',
      '--text-color': card.textColor,
      ...useEditorFontFamily(card.textFontFamily),
      fontWeight: card.fontWeight,
      textAlign: card.textAlign,
    }"
  >
    <div
      class="card-item-text__body marked min-w-0 max-w-full flex-1"
      v-html="markedText"
    />
    <div
      v-if="button"
      class="card-item-text__button mt-auto flex min-w-0 max-w-full w-full overflow-hidden"
      :class="buttonAlignmentClasses"
    >
      <CardsDecorativeButton :button="button" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ButtonStyleInterface } from '../common'
import { getCardPaddingClass, type CardPaddingSize } from '../card-padding'
import CardsDecorativeButton from './CardsDecorativeButton.vue'

type ButtonWidth = 'content' | 'full' | 'pixels'
type ButtonAlign = 'text' | 'start' | 'center' | 'end'

interface DecorativeButtonInterface
  extends Partial<Omit<ButtonStyleInterface, 'type'>> {
  widthDesktop?: ButtonWidth
  widthDesktopPx?: number
  widthMobile?: ButtonWidth
  widthMobilePx?: number
}

interface CardInterface {
  textColor?: string
  textFontFamily?: string
  fontWeight?: string
  textAlign?: 'start' | 'center' | 'end'
  bgColor?: string
  isButtonShown?: boolean
  buttonLook?: 'solid' | 'border'
  buttonColor?: string
  buttonColorHover?: string
  buttonTextColor?: string
  buttonTextFontFamily?: string
  buttonText?: string
  buttonWidthDesktop?: ButtonWidth
  buttonWidthDesktopPx?: number
  buttonWidthMobile?: ButtonWidth
  buttonWidthMobilePx?: number
  buttonAlignDesktop?: ButtonAlign
  buttonAlignMobile?: ButtonAlign
  // Legacy settings used as fallbacks for existing cards.
  isButtonFullWidth?: boolean
  buttonAlign?: ButtonAlign
}

interface PropsInterface {
  card: CardInterface
  markedText: string
  horizontalPadding?: CardPaddingSize
  verticalPadding?: CardPaddingSize
}

const props = defineProps<PropsInterface>()

const button = computed<DecorativeButtonInterface | undefined>(() => {
  if (!props.card.isButtonShown) {
    return undefined
  }

  return {
    look: props.card.buttonLook,
    color: props.card.buttonColor,
    colorHover: props.card.buttonColorHover,
    textColor: props.card.buttonTextColor,
    textFontFamily: props.card.buttonTextFontFamily,
    text: props.card.buttonText,
    widthDesktop:
      props.card.buttonWidthDesktop ||
      (props.card.isButtonFullWidth ? 'full' : 'content'),
    widthDesktopPx: props.card.buttonWidthDesktopPx,
    widthMobile:
      props.card.buttonWidthMobile ||
      (props.card.isButtonFullWidth ? 'full' : 'content'),
    widthMobilePx: props.card.buttonWidthMobilePx,
  }
})

const horizontalPaddingClass = computed(() =>
  getCardPaddingClass(props.horizontalPadding, 'horizontal', 'base', 'sm')
)

const verticalPaddingClass = computed(() =>
  getCardPaddingClass(props.verticalPadding, 'vertical', 'base', 'xs')
)

const resolveButtonAlignment = (alignment?: ButtonAlign) => {
  const resolvedAlignment = alignment || props.card.buttonAlign || 'text'

  return resolvedAlignment === 'text'
    ? props.card.textAlign || 'start'
    : resolvedAlignment
}

const buttonAlignmentClasses = computed(() => {
  const mobileAlignment = resolveButtonAlignment(props.card.buttonAlignMobile)
  const desktopAlignment = resolveButtonAlignment(props.card.buttonAlignDesktop)

  const mobileClass = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
  }[mobileAlignment]
  const desktopClass = {
    start: 'md:justify-start',
    center: 'md:justify-center',
    end: 'md:justify-end',
  }[desktopAlignment]

  return [mobileClass, desktopClass]
})
</script>

<style scoped>
.card-item-text :deep(img) {
  display: inline-block;
}

.card-item-text__body {
  overflow-wrap: anywhere;
}
</style>
